import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useCallback, useEffect, useLayoutEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useLocation, useNavigate } from 'react-router-dom';
import { CoachmarkOverlay } from '../coachmark/Coachmark';
import { Button } from '../ui/Button';
import { downloadExtensionPackageOrOpenInstallHelp } from '../../utils/extensionPackageDownload';
import { useExtensionInstalled } from '../../hooks/useExtensionInstalled';
import { GUIDE_PILLS, getWalkthroughStepPanel, renderEmphasisText, stripEmphasisForCoachmark, } from '../../features/onboarding/welcomeWalkthroughCopy';
import { WALKTHROUGH_TOTAL_STEPS } from '../../features/onboarding/walkthroughConstants';
import { dashboardPanelHref, isDashboardLibraryKanbanView } from '../../constants/dashboardPanels';
import '../../styles/components/dashboard.css';
export { WALKTHROUGH_TOTAL_STEPS } from '../../features/onboarding/walkthroughConstants';
/**
 * Resolves a stable anchor id for the current step and route, or null if the
 * user must navigate first (e.g. library step while still on the dashboard is handled
 * by navigation effects).
 */
export function getWalkthroughAnchorId(step, pathname) {
    const p = pathname || '/';
    if (step <= 3) {
        if (p !== '/' && p !== '')
            return null;
        const homeIds = [
            'wt-dash-greeting-title',
            'wt-dash-extension',
            'wt-dash-extension',
            'wt-dash-library',
        ];
        return homeIds[step] ?? null;
    }
    if (step === 4) {
        if (p === '/' || p === '' || p.startsWith('/library')) {
            return 'wt-library-main';
        }
        return null;
    }
    if (step === 5) {
        if (p === '/' || p === '' || p.startsWith('/reviews')) {
            return 'wt-reviews-main';
        }
        return null;
    }
    if (step === 6) {
        if (!p.startsWith('/templates'))
            return null;
        return 'wt-templates-main';
    }
    return null;
}
export default function WelcomeWalkthrough({ open, currentStep, totalSteps, onSelectStep, onNext, onPrevious, onSkip, onClose, onComplete, }) {
    const navigate = useNavigate();
    const location = useLocation();
    const extensionInstalled = useExtensionInstalled(open);
    const [coachmarkTarget, setCoachmarkTarget] = useState(null);
    const step = Math.min(currentStep, totalSteps - 1);
    const isFirst = currentStep === 0;
    const pathname = location.pathname || '/';
    const search = location.search ?? '';
    // After advancing, move to the right route (step 3 → 4 on home, etc.)
    useEffect(() => {
        if (!open)
            return;
        if (currentStep !== 4)
            return;
        const params = new URLSearchParams(search);
        const onLibraryPanel = (pathname === '/' || pathname === '') && params.get('panel') === 'library';
        if (pathname.startsWith('/library') || onLibraryPanel) {
            return;
        }
        if (pathname === '/' || pathname === '') {
            navigate(dashboardPanelHref('library'), { replace: true });
        }
    }, [open, currentStep, pathname, navigate, search]);
    useEffect(() => {
        if (!open)
            return;
        if (currentStep !== 5)
            return;
        const params = new URLSearchParams(search);
        const onReviewsPanel = pathname.startsWith('/reviews') ||
            ((pathname === '/' || pathname === '') && isDashboardLibraryKanbanView(params));
        if (onReviewsPanel) {
            return;
        }
        if (pathname === '/' || pathname === '' || pathname.startsWith('/library')) {
            navigate(dashboardPanelHref('reviews'), { replace: true });
        }
    }, [open, currentStep, pathname, navigate, search]);
    useEffect(() => {
        if (!open)
            return;
        const params = new URLSearchParams(search);
        const onReviews = pathname.startsWith('/reviews') ||
            ((pathname === '/' || pathname === '') && isDashboardLibraryKanbanView(params));
        if (currentStep === 6 &&
            totalSteps === WALKTHROUGH_TOTAL_STEPS &&
            onReviews &&
            !pathname.startsWith('/templates')) {
            navigate('/templates', { replace: true });
        }
    }, [open, currentStep, pathname, navigate, totalSteps, search]);
    const templatesTourEnabled = totalSteps === WALKTHROUGH_TOTAL_STEPS;
    const { kicker, title, body, primaryLabel, showInstallSkip, coachmarkPlacement, coachKicker } = useMemo(() => getWalkthroughStepPanel(step, extensionInstalled, templatesTourEnabled), [step, extensionInstalled, templatesTourEnabled]);
    const guidePills = useMemo(() => GUIDE_PILLS.slice(0, totalSteps), [totalSteps]);
    useLayoutEffect(() => {
        if (!open) {
            setCoachmarkTarget(null);
            return;
        }
        const raf = window.requestAnimationFrame(() => {
            const id = getWalkthroughAnchorId(step, pathname);
            if (!id) {
                setCoachmarkTarget(null);
                return;
            }
            const el = document.getElementById(id);
            setCoachmarkTarget(el);
            if (el) {
                el.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
        });
        return () => window.cancelAnimationFrame(raf);
    }, [open, step, pathname, search]);
    const coachmarkItems = useMemo(() => {
        if (!open || !coachmarkTarget) {
            return [];
        }
        const desc = stripEmphasisForCoachmark(body);
        return [
            {
                key: `wt-step-${step}`,
                target: coachmarkTarget,
                title: coachKicker,
                description: desc,
                placement: coachmarkPlacement,
            },
        ];
    }, [open, coachmarkTarget, step, body, coachKicker, coachmarkPlacement]);
    const handlePrimary = useCallback(() => {
        if (step === 0) {
            onNext();
            return;
        }
        if (step === 1) {
            if (extensionInstalled) {
                onNext();
                return;
            }
            downloadExtensionPackageOrOpenInstallHelp();
            return;
        }
        if (step === 2) {
            onNext();
            return;
        }
        if (step === 3) {
            onNext();
            return;
        }
        if (step === 4) {
            onNext();
            return;
        }
        if (step === 5) {
            onNext();
            return;
        }
        onComplete();
        onClose();
    }, [step, extensionInstalled, onNext, onComplete, onClose]);
    if (!open) {
        return null;
    }
    const bodyEl = typeof document !== 'undefined' ? document.body : null;
    const tourCard = (_jsxs("section", { className: "welcome-walkthrough-guide welcome-walkthrough-guide--floating", "aria-label": "Cricket Studio quick tour", children: [_jsx("div", { className: "welcome-walkthrough-guide-kicker", children: "New to Cricket Studio?" }), _jsxs("div", { className: "welcome-walkthrough-guide-body", children: [_jsxs("div", { children: [kicker ? _jsx("p", { className: "welcome-walkthrough-guide-step-kicker", children: kicker }) : null, _jsx("h3", { className: "welcome-walkthrough-guide-title", children: title })] }), _jsx("div", { className: "welcome-walkthrough-guide-points", role: "tablist", "aria-label": "Tour topics", children: guidePills.map((pill, index) => (_jsx("button", { type: "button", className: `welcome-walkthrough-guide-point${index === currentStep ? ' is-active' : ''}`, onClick: () => onSelectStep(index), "aria-pressed": index === currentStep, children: pill.shortLabel }, pill.shortLabel))) }), _jsx("p", { className: "welcome-walkthrough-guide-text", id: "walkthrough-step-detail", children: renderEmphasisText(body) }), guidePills[step] ? (_jsx("p", { className: "welcome-walkthrough-guide-hint", "aria-hidden": "true", children: guidePills[step].help })) : null] }), _jsxs("div", { className: "welcome-walkthrough-guide-actions", children: [_jsx(Button, { variant: "ghost", size: "sm", onClick: onSkip, children: "Skip tour" }), _jsx("span", { className: "welcome-walkthrough-guide-spacer" }), _jsx(Button, { variant: "secondary", size: "sm", onClick: onClose, children: "Close" }), !isFirst && (_jsx(Button, { variant: "secondary", size: "sm", onClick: onPrevious, children: "Back" })), showInstallSkip ? (_jsx(Button, { variant: "secondary", size: "sm", onClick: onNext, children: "I installed it \u2014 next" })) : null, _jsx(Button, { variant: "primary", size: "sm", onClick: handlePrimary, children: primaryLabel })] })] }));
    return (_jsxs(_Fragment, { children: [_jsx(CoachmarkOverlay, { items: coachmarkItems, onDismiss: () => { }, scrimDismissable: false, escapeDismissable: false, showTooltipCloseButton: false }), bodyEl ? createPortal(tourCard, bodyEl) : null] }));
}
